// components/ReviewTemplateList/TemplateView/index.tsx
import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Row, Col, Form, FormGroup, Input, Label, Card, CardBody, CardHeader, Button } from 'reactstrap';
import { Btn, H5, P } from '../../../AbstractElements';
import { useFetchTemplateWithAssignment } from '../../../hooks/bookReviewTemplate/useFetchTemplateWithAssignment';
import { useReviewResponses } from '../../../hooks/bookReviewTemplate/useReviewResponses';

type AnswerShape = {
    section: 'pre' | 'during' | 'post';
    index: number;
    label?: string;
    value: any;
};

function QuestionRow({ q, field }: { q: any; field: any }) {
    const type = q.type;
    const options = q.options || [];

    if (type === 'textarea' || type === 'timestampedNotes') {
        return (
            <Input
                type="textarea"
                rows={4}
                placeholder={q.placeholder || ''}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                innerRef={field.ref}
            />
        );
    }

    if (type === 'select') {
        return (
            <Input
                type="select"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                innerRef={field.ref}
            >
                <option value="">— select —</option>
                {options.map((o: any) => (
                    <option key={o.value} value={o.value}>
                        {o.label ?? o.value}
                    </option>
                ))}
            </Input>
        );
    }

    if (type === 'multi') {
        return (
            <Input
                type="select"
                multiple
                value={Array.isArray(field.value) ? field.value : []}
                onChange={(e) => {
                    const sel = Array.from((e.target as any)?.selectedOptions).map((o: any) => o.value);
                    field.onChange(sel);
                }}
                innerRef={field.ref}
            >
                {options.map((o: any) => (
                    <option key={o.value} value={o.value}>
                        {o.label ?? o.value}
                    </option>
                ))}
            </Input>
        );
    }

    if (type === 'boolean') {
        return (
            <div className="d-flex align-items-center">
                <Input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    innerRef={field.ref}
                    style={{ width: 18, height: 18 }}
                />
                <span style={{ marginLeft: 8 }}>{q.label || ''}</span>
            </div>
        );
    }

    if (type === 'number') {
        return (
            <Input
                type="number"
                placeholder={q.placeholder || ''}
                value={field.value ?? ''}
                onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v === '' ? '' : Number(v));
                }}
                innerRef={field.ref}
            />
        );
    }

    if (type === 'date') {
        return (
            <Input
                type="date"
                value={field.value ? String(field.value).split('T')[0] : ''}
                onChange={(e) => field.onChange(e.target.value)}
                innerRef={field.ref}
            />
        );
    }

    if (type === 'rating') {
        return (
            <Input
                type="select"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(Number(e.target.value))}
                innerRef={field.ref}
            >
                <option value="">— rate —</option>
                {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>
                        {n}
                    </option>
                ))}
            </Input>
        );
    }

    return (
        <Input
            type="text"
            placeholder={q.placeholder || ''}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            innerRef={field.ref}
        />
    );
}

export default function ReviewTemplateView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { template, loading, error, refetch } = useFetchTemplateWithAssignment(id);
    const { search } = useLocation();

    const firstAssignment = template?.assignments && template.assignments.length > 0 ? template.assignments[0] : null;

    // hide Draft/Submit buttons if due date passed
    const isPastDue = useMemo(() => {
        const due = firstAssignment?.dueAt ?? null;
        if (!due) return false;
        try {
            const dueDate = new Date(due);
            // dueDate < now -> past due
            return dueDate.getTime() < Date.now();
        } catch {
            return false;
        }
    }, [firstAssignment?.dueAt]);

    // hide Draft/Submit while visibleFrom is in the future (not yet open)
    const isNotYetVisible = useMemo(() => {
        const vf = firstAssignment?.visibleFrom ?? null;
        if (!vf) return false; // no visibleFrom => assume visible
        try {
            const visibleFromDate = new Date(vf);
            return visibleFromDate.getTime() > Date.now();
        } catch {
            return false;
        }
    }, [firstAssignment?.visibleFrom]);

    // whether the participant can interact (not past due and already visible)
    const canInteract = !isPastDue && !isNotYetVisible;

    function extractFromQueryParam(keyName: string) {
        const qp = new URLSearchParams(search);
        return qp.get(keyName); // null if not present
    }

    const participantId = extractFromQueryParam("participantId");
    const assignmentId = extractFromQueryParam("assignmentId");

    // hook that handles reviews (fetch existing, save draft, submit)
    const {
        existingReview,
        initialResponses,
        loading: reviewLoading,
        saving,
        saveDraft,
        submitReview,
        refetch: refetchReview
    } = useReviewResponses(template?._id, assignmentId || firstAssignment?._id, String(participantId));

    // detect whether current user is participant (from localStorage)
    const currentUser = (() => {
        try {
            return JSON.parse(String(localStorage.getItem('user') || '{}'));
        } catch {
            return {};
        }
    })();
    // role might be stored as string or object { name: 'participant' }
    const currentRoleName = (currentUser?.role && typeof currentUser.role === 'object') ? currentUser.role.name : currentUser?.role;
    const isParticipant = String(currentRoleName || '').toLowerCase() === 'participant';

    // build sensible default values for the form once template is loaded
    const defaultValues = useMemo(() => {
        if (!template) return undefined;
        const source = template; // prefer template or snapshot if desired

        const mapSection = (arr: any[] = []) =>
            arr.map((q) => {
                let defaultVal: any = '';
                if (q.type === 'multi') defaultVal = [];
                else if (q.type === 'boolean') defaultVal = false;
                else if (q.type === 'number') defaultVal = '';
                else defaultVal = '';
                return { value: defaultVal };
            });

        const base = {
            title: source.title ?? '',
            description: source.description ?? '',
            tags: source.tags ?? [],
            sections: {
                pre: mapSection(source.sections?.pre ?? []),
                during: mapSection(source.sections?.during ?? []),
                post: mapSection(source.sections?.post ?? []),
            },
        } as any;

        // overlay initialResponses if present (initialResponses is a flat map like { 'pre.0': '...' })
        if (initialResponses) {
            (['pre', 'during', 'post'] as const).forEach((sec) => {
                const arr = base.sections[sec] ?? [];
                arr.forEach((item: any, idx: number) => {
                    const key = `${sec}.${idx}`;
                    if (Object.prototype.hasOwnProperty.call(initialResponses, key)) {
                        base.sections[sec][idx].value = initialResponses[key];
                    }
                });
            });
        }

        return base;
    }, [template, initialResponses]);

    const { control, reset, handleSubmit, getValues, formState: { errors } } = useForm<any>({
        mode: 'onChange',
        defaultValues: defaultValues ?? {}
    });

    // helper: readable visibleFrom string if present
    const visibleFromLabel = useMemo(() => {
        if (!firstAssignment?.visibleFrom) return null;
        try {
            return new Date(firstAssignment.visibleFrom).toLocaleString();
        } catch {
            return String(firstAssignment.visibleFrom);
        }
    }, [firstAssignment?.visibleFrom]);


    // reset whenever the template or initialResponses change
    useEffect(() => {
        if (!defaultValues) return;
        reset(defaultValues);
    }, [defaultValues, reset]);

    if (loading || reviewLoading) return <div style={{ padding: 24 }}>Loading template...</div>;
    if (error) return <div style={{ padding: 24, color: 'red' }}>{error}</div>;
    if (!template) return <div style={{ padding: 24 }}>Template not found.</div>;

    const source = template;

    // disable draft button when the review already submitted
    const disableDraftAfterSubmit = existingReview?.status === 'submitted';

    // Submit handler: will run react-hook-form validation rules
    const onSubmit = async (formData: any) => {
        try {
            await submitReview(formData);
            // refresh data after submit
            refetchReview();
        } catch (e) {
            // hook should handle errors & toasts; keep fallback
            console.error('submit error', e);
        }
    };

    // Save Draft handler: IMPORTANT -> bypass validation; get raw form values
    const onSaveDraft = async () => {
        // get current values without triggering validation
        const vals = getValues();
        try {
            await saveDraft(vals);
            // refresh after save
            refetchReview();
        } catch (e) {
            console.error('save draft error', e);
        }
    };

    // Reset to default values (which include any initialResponses)
    const onReset = () => {
        if (defaultValues) reset(defaultValues);
    };


    return (
        <div style={{ maxWidth: 960, margin: '0px auto', padding: 12 }}>
            <Card className="mb-3">
                <CardBody>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div>
                            <H5 style={{ margin: 0 }}>{source.title}</H5>
                            <div style={{ color: '#666', fontSize: 13 }}>{source.description}</div>
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn color="secondary" onClick={() => navigate(-1)}>Back</Btn>
                            <Btn color="primary" onClick={() => { refetch && refetch(id); refetchReview(); }}>Refresh</Btn>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {firstAssignment && (
                <Card className="mb-3">
                    <CardHeader>
                        <H5 className="mb-0">Book Review</H5>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <Col md={6}>
                                {/* <P><strong>Group:</strong> {firstAssignment.group?.title || '—'}</P> */}
                                <P>
                                    <strong>Title:</strong>{' '}
                                    {`Available from ${firstAssignment?.visibleFrom ? new Date(String(firstAssignment.visibleFrom)).toLocaleString().split(',')[0] : '—'} to ${firstAssignment?.visibleTo ? new Date(String(firstAssignment.visibleTo)).toLocaleString().split(',')[0] : '—'}`}
                                </P>
                                <P><strong>Assigned At:</strong> {firstAssignment?.assignedAt ? new Date(String(firstAssignment.assignedAt)).toLocaleString().split(',')[0] : '—'}
                                </P>
                            </Col>
                            <Col md={6}>
                             <P><strong>Due At:</strong> {firstAssignment?.dueAt ? new Date(String(firstAssignment.dueAt)).toLocaleString().split(',')[0] : '—'}
                                </P>
                                {/* <P><strong>Visible From:</strong> {firstAssignment.visibleFrom ? new Date(firstAssignment.visibleFrom).toLocaleString() : '—'}</P>
                                <P><strong>Visible To:</strong> {firstAssignment.visibleTo ? new Date(firstAssignment.visibleTo).toLocaleString() : '—'}</P> */}
                                <P><strong>Status:</strong> {firstAssignment.status || '—'}</P>
                            </Col>
                        </Row>

                        {/* Show small notice if due date passed */}
                        {isPastDue && (
                            <div style={{ marginTop: 10, padding: 10, background: '#fff6f6', borderRadius: 6, border: '1px solid #ffd2d2' }}>
                                <strong style={{ color: '#b33' }}>Due date passed — responses are closed.</strong>
                            </div>
                        )}

                        {/* Show notice if assignment not open yet */}
                        {!isPastDue && isNotYetVisible && (
                            <div style={{ marginTop: 10, padding: 10, background: '#fff9f0', borderRadius: 6, border: '1px solid #ffecd1' }}>
                                <strong style={{ color: '#b36b00' }}>Assignment not open yet.</strong>
                                <div style={{ color: '#6b4f00', marginTop: 6 }}>
                                    This assignment will be visible from <strong>{visibleFromLabel}</strong>. You won't be able to save or submit responses until then.
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}

            <Form onSubmit={handleSubmit(onSubmit)}>
                {(['pre', 'during', 'post'] as const).map((sec) => {
                    const secQuestions = source.sections?.[sec] || [];
                    return (
                        <Card className="mb-3" key={sec}>
                            <CardHeader>
                                <H5 className="mb-0">{sec === 'pre' ? 'Pre Discussion Questions' : sec === 'during' ? 'During Discussion Questions' : 'Post Discussion Questions'}</H5>
                            </CardHeader>
                            <CardBody>
                                {secQuestions.length === 0 && <div style={{ color: '#666' }}>No questions.</div>}

                                {secQuestions.map((q: any, idx: number) => {
                                    const fieldName = `sections.${sec}.${idx}.value`;

                                    // validation rules apply on submit only; draft skips validation because we bypass it
                                    const validationRules = q.required ? { required: `${q.label || 'This question'} is required` } : {};

                                    // We show error if rf-form flags it on submit attempt
                                    const sectionErrors = (errors?.sections as Record<string, any> | undefined);
                                    const fieldError = sectionErrors?.[sec]?.[idx]?.value ?? null;

                                    return (
                                        <FormGroup key={idx}>
                                            <Label className="mb-1">{q.label}{q.required ? ' *' : ''}</Label>
                                            <Controller
                                                control={control}
                                                name={fieldName}
                                                rules={validationRules}
                                                defaultValue={q.type === 'multi' ? [] : (q.type === 'boolean' ? false : '')}
                                                render={({ field }) => <QuestionRow q={q} field={field} />}
                                            />
                                            {q.placeholder && <div style={{ color: '#888', marginTop: 6 }}>{q.placeholder}</div>}
                                            {fieldError && <div style={{ color: '#d9534f', marginTop: 6 }}>{String(fieldError.message || fieldError)}</div>}
                                        </FormGroup>
                                    );
                                })}
                            </CardBody>
                        </Card>
                    );
                })}

                {/* Only participants should see action buttons */}
                {isParticipant && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button color="secondary" type="button" onClick={onReset}>Reset</Button>

                        {/* show buttons only when allowed (not past due and visibleFrom reached) */}
                        {canInteract && (
                            <>
                                <Button
                                    color="warning"
                                    type="button"
                                    onClick={onSaveDraft}
                                    disabled={saving || disableDraftAfterSubmit}
                                    title={disableDraftAfterSubmit ? 'Cannot save draft after submission' : undefined}
                                >
                                    {saving ? 'Saving...' : (disableDraftAfterSubmit ? 'Draft disabled' : 'Save Draft')}
                                </Button>

                                <Button color="success" type="submit" disabled={saving || disableDraftAfterSubmit}>
                                    {saving ? 'Submitting...' : 'Submit Responses'}
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </Form>
        </div>
    );
}
